import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  FaPlusCircle,
  FaMinusCircle,
  FaEdit,
  FaTrash,
  FaTimes,
  FaSearch,
  FaFileExport,
  FaSave,
  FaSignOutAlt,
  FaFilePrescription,
  FaInfoCircle,
  FaPlus,
  FaCheck,
  FaArrowLeft
} from "react-icons/fa";
import ConfirmModal from "../../UI/ConfirmModal";
import classes from "./IPDPatientDischarge.module.css";
import AdmissionDetailsModal from "./Pages/AdmissionDetailsModal";
import DischargeModal from "./Pages/DischargeModal";
import IPDPatientDischargePrint, { printIPDPatientDischarge } from "./Pages/IPDPatientDischargePrint";
import PrescriptionModal from "./Pages/PrescriptionModal";
import { exportIPDPrescription, printIPDPrescription } from "./Pages/IPDPrescriptionPrint";
import { getAuthToken } from "../../../util/auth";
import { getApiChara } from "../../../util/getApi";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { exportTreatmentPDF } from "./Pages/TreatmentExportPDF";
import IPDPrescriptionSearchModal from "./Pages/IPDPrescriptionSearchModal";
import VitalsTrendChart from "./Pages/VitalsTrendChart";
import AuthContext from "../../../store/auth-context";

// 🔹 Simple Validation Modal
const ValidationModal = ({ show, errors, onClose }) => {
  const { t } = useTranslation();
  if (!show) return null;
  return (
    <div className={classes.modalOverlay}>
      <div className={classes.modalContent}>
        <h3 style={{ color: 'var(--primary-color)' }}>{t("Validation Errors")}</h3>
        <ul>
          {errors.map((err, idx) => (
            <li key={idx} className={classes.errorItem}>
              {err}
            </li>
          ))}
        </ul>
        <button onClick={onClose} className="btn-secondary-premium">
          <FaTimes className="me-2" /> {t("Close")}
        </button>
      </div>
    </div>
  );
};

const IPDPatientDischarge = () => {
  const { t } = useTranslation();
  const authCtx = useContext(AuthContext);
  const hospitalData = authCtx.hospitalData;
  const { ipdPatientId } = useParams(); // This is the patientId from URL
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [admissionData, setAdmissionData] = useState(null);
  const [historicTreatments, setHistoricTreatments] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [prescribedMedicines, setPrescribedMedicines] = useState([]);
  const [isSavePrescription, setIsSavePrescription] = useState(false);
  const [isPrintPrescription, setIsPrintPrescription] = useState(false);
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [diagnosisList, setDiagnosisList] = useState([]);

  // Helper to get local ISO date/time parts
  const getNow = () => {
    const now = new Date();
    const date = now.toISOString().split("T")[0];
    const time = now.toTimeString().slice(0, 5);
    return { date, time };
  };

  const [form, setForm] = useState({
    date: getNow().date,
    time: getNow().time,
    bpSystolic: "",
    bpDiastolic: "",
    temp: "",
    pulse: "",
    spo2: "",
    weight: "",
    height: "",
    rr: "",
    bsl: "",
    diagnosis: "",
    complaint: "",
    remarks: "",   // Doctor's Remarks
    details: [{ type: "", name: "", dose: "", instructions: "", allergy: "Yes" }],
  });

  const [showModal, setShowModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationModal, setShowValidationModal] = useState(false);
  // Confirmation Modal State
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showDischargeModal, setShowDischargeModal] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [editHistoricId, setEditHistoricId] = useState(null);
  const [userRole, setUserRole] = useState(authCtx.role);

  const fetchAdmission = async () => {
    const token = getAuthToken();
    if (!token || token === "EXPIRED") return;

    setIsLoading(true);
    try {
      const apiChar = getApiChara();
      const response = await fetch(`${apiChar}/ipd/admissions/${ipdPatientId}`, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!response.ok) throw new Error("Failed to fetch admission data");
      const currentAdmission = await response.json();

      if (currentAdmission && currentAdmission.ipdAdmissionId) {
        setAdmissionData(currentAdmission);
        setForm(prev => ({
          ...prev,
          height: currentAdmission.height || "",
          diagnosis: currentAdmission.diagnosis || ""
        }));
        fetchHistoricTreatments(currentAdmission.ipdAdmissionId);
      } else {
        toast.error("Patient admission data not found");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error loading patient details");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchHistoricTreatments = async (admissionId) => {
    const token = getAuthToken();
    if (!token || token === "EXPIRED" || !admissionId) return;

    try {
      const apiChar = getApiChara();
      const response = await fetch(`${apiChar}/ipd/vitals/admission/${admissionId}`, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!response.ok) throw new Error("Failed to fetch historic treatments");
      const data = await response.json();
      setHistoricTreatments(data || []);
    } catch (err) {
      console.error("Historical data error:", err);
    }
  };

  // ✅ Fetch Admission Details on load
  useEffect(() => {
    if (ipdPatientId) {
      fetchAdmission();
    }
  }, [ipdPatientId]);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleDetailChange = (index, e) => {
    const { name, value } = e.target;

    setForm(prev => {
      const updated = [...prev.details];
      updated[index][name] = value;

      if (name === "name") {
        handleMedicineSearch(value, index);

        // Auto-fill Type if matched in current results
        if (value && searchResults.length > 0) {
          const found = searchResults.find(m =>
            m.name && m.name.trim().toUpperCase() === value.trim().toUpperCase()
          );

          if (found && found.medType) {
            const allowedTypes = [
              "TAB", "CAP", "SYR", "SUSP", "SAC", "INJ", "DROP", "EDRP", "LIQ", "OINT", "CRE", "GEL", "LOT", "SPRAY", "SUPP", "ROTACAP"
            ];
            let medTypeUpper = found.medType.toUpperCase();

            // Map full names to abbreviations
            if (medTypeUpper === "TABLET") medTypeUpper = "TAB";
            if (medTypeUpper === "CAPSULE") medTypeUpper = "CAP";
            if (medTypeUpper === "SYRUP") medTypeUpper = "SYR";
            if (medTypeUpper === "SUSPENSION") medTypeUpper = "SUSP";
            if (medTypeUpper === "INJECTION") medTypeUpper = "INJ";

            if (allowedTypes.includes(medTypeUpper)) {
              updated[index]["type"] = medTypeUpper;
            }
          }
        }
      }
      return { ...prev, details: updated };
    });
  };

  const handleMedicineSearch = async (term, rowIndex = null) => {
    if (!term || term.length < 1) {
      setSearchResults([]);
      return;
    }
    const token = getAuthToken();
    if (!token || token === "EXPIRED") return;

    try {
      const apiChar = getApiChara();
      const response = await fetch(`${apiChar}/medicine/getMedicinesByNameOrBarcodeOrDisease?name=${encodeURIComponent(term)}`, {
        headers: { Authorization: "Bearer " + token },
      });
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data || []);

        // Auto-fill for targeted row if perfect match found in fresh results
        if (rowIndex !== null && data.length > 0) {
          const found = data.find(m => m.name && m.name.trim().toUpperCase() === term.trim().toUpperCase());
          if (found && found.medType) {
            const allowedTypes = [
              "TAB", "CAP", "SYR", "SUSP", "SAC", "INJ", "DROP", "EDRP", "LIQ", "OINT", "CRE", "GEL", "LOT", "SPRAY", "SUPP", "ROTACAP"
            ];
            let medTypeUpper = found.medType.toUpperCase();

            // Map full names to abbreviations
            if (medTypeUpper === "TABLET") medTypeUpper = "TAB";
            if (medTypeUpper === "CAPSULE") medTypeUpper = "CAP";
            if (medTypeUpper === "SYRUP") medTypeUpper = "SYR";
            if (medTypeUpper === "SUSPENSION") medTypeUpper = "SUSP";
            if (medTypeUpper === "INJECTION") medTypeUpper = "INJ";

            if (allowedTypes.includes(medTypeUpper)) {
              setForm(prev => {
                const updated = [...prev.details];
                // Only update if still same name
                if (updated[rowIndex].name && updated[rowIndex].name.trim().toUpperCase() === term.trim().toUpperCase()) {
                  updated[rowIndex].type = medTypeUpper;
                }
                return { ...prev, details: updated };
              });
            }
          }
        }
      }
    } catch (err) {
      console.error("Medicine search error:", err);
    }
  };

  const fetchDiagnosisSuggestions = async (term) => {
    if (!term || term.trim() === "") {
      setDiagnosisList([]);
      return;
    }
    const token = getAuthToken();
    if (!token || token === "EXPIRED") return;

    try {
      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/diagnosis/${encodeURIComponent(term)}`;
      const response = await fetch(apiUrl, {
        headers: { Authorization: "Bearer " + token },
      });
      if (response.ok) {
        const data = await response.json();
        setDiagnosisList(data.map((d) => d.diagnosisName));
      }
    } catch (err) {
      console.error("Diagnosis search error:", err);
    }
  };

  const handleDiagnosisChange = (e) => {
    const value = e.target.value;
    setForm(prev => ({ ...prev, diagnosis: value }));
    fetchDiagnosisSuggestions(value);
  };

  const handleSearchSavedPrescriptions = async () => {
    if (!form.diagnosis.trim()) {
      toast.warn("Please enter a diagnosis to search prescriptions");
      return;
    }
    const token = getAuthToken();
    const apiChar = getApiChara();
    const apiUrl = `${apiChar}/ipd/saved-prescriptions/diagnosis/${encodeURIComponent(form.diagnosis.trim())}`;

    try {
      const response = await fetch(apiUrl, {
        headers: { Authorization: "Bearer " + token }
      });
      if (!response.ok) throw new Error("Fetch failed");
      const data = await response.json();
      if (Object.keys(data).length === 0) {
        toast.info("No saved prescriptions found for this diagnosis.");
      } else {
        toast.success("Prescriptions found! Use Prescription button to add them.");
        setShowPrescriptionModal(true);
      }
    } catch (error) {
      console.error(error);
      toast.error("Error searching prescriptions");
    }
  };

  const handleAddDetailRow = () => {
    setForm({
      ...form,
      details: [
        ...form.details,
        { type: "", name: "", dose: "", instructions: "", allergy: "" },
      ],
    });
  };

  const handleRemoveDetailRow = (index) => {
    const updated = [...form.details];
    updated.splice(index, 1);
    setForm({ ...form, details: updated });
  };

  const validateForm = () => {
    const errors = [];
    if (!form.date) errors.push("Date is required");
    if (!form.time) errors.push("Time is required");
    return errors;
  };

  const handleAddTreatment = () => {
    const errors = validateForm();

    // Check if the overall record is "empty" (no vitals, no complaints, no remarks, no medications)
    const hasVitals = form.bpSystolic || form.bpDiastolic || form.temp || form.pulse || form.spo2 || form.rr || form.bsl || form.weight || form.height;
    const hasNotes = form.complaint || form.remarks || form.diagnosis;
    const validDetails = form.details.filter(d => d.type || d.name || d.dose || d.instructions);
    const hasMedications = validDetails.length > 0;

    if (!hasVitals && !hasNotes && !hasMedications) {
      toast.error("Cannot add an empty record. Please enter vitals, remarks, or medications.");
      return;
    }

    if (errors.length > 0) {
      setValidationErrors(errors);
      setShowValidationModal(true);
      return;
    }

    // Filter out completely empty medication rows before adding
    const cleanedForm = {
      ...form,
      details: validDetails.length > 0 ? validDetails : [{ type: "", name: "", dose: "", instructions: "", allergy: "" }]
    };

    if (editHistoricId !== null) {
      // Update historic record
      const token = getAuthToken();
      const apiChar = getApiChara();

      const medDetails = cleanedForm.details
        .filter(d => d.name)
        .map(d => `${d.type ? d.type + ": " : ""}${d.name}${d.dose ? ` (${d.dose})` : ""}${d.instructions ? ` - ${d.instructions}` : ""}`)
        .join("; ");

      const combinedDetails = [
        cleanedForm.diagnosis ? `Diagnosis: ${cleanedForm.diagnosis}` : "",
        cleanedForm.complaint ? `Complaint: ${cleanedForm.complaint}` : "",
        cleanedForm.remarks ? `Remarks: ${cleanedForm.remarks}` : "",
        medDetails ? `Prescription: ${medDetails}` : ""
      ].filter(Boolean).join(" | ");

      const vitalDateTime = `${cleanedForm.date} ${cleanedForm.time}:00`;

      const payload = {
        id: editHistoricId,
        ipdAdmissionId: admissionData,
        vitalDateTime: vitalDateTime,
        details: combinedDetails || "N/A",
        weight: String(cleanedForm.weight || admissionData.weight || ""),
        bpSystolic: Number(cleanedForm.bpSystolic) || 0,
        bpDiastolic: Number(cleanedForm.bpDiastolic) || 0,
        pulse: Number(cleanedForm.pulse) || 0,
        temperature: Number(cleanedForm.temp) || 0,
        spo2: Number(cleanedForm.spo2) || 0,
        notes: cleanedForm.remarks || cleanedForm.complaint || "",
        option1: cleanedForm.rr || "",
        option2: cleanedForm.bsl || "",
        option3: ""
      };

      setIsLoading(true);
      fetch(`${apiChar}/ipd/vitals`, { // Assuming POST/PUT handles it, usually save handles update if ID exists, testing with POST as originally used, if fails might need PUT endpoint
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })
        .then(res => {
          if (!res.ok) throw new Error("Update failed");
          toast.success("Record updated successfully");
          fetchHistoricTreatments(admissionData.ipdAdmissionId);
          resetForm();
          setEditHistoricId(null);
        })
        .catch(err => {
          console.error(err);
          toast.error("Error updating record");
        })
        .finally(() => setIsLoading(false));

    } else if (editIndex !== null) {
      const updated = [...treatments];
      updated[editIndex] = cleanedForm;
      setTreatments(updated);
      setEditIndex(null);
      resetForm();
    } else {
      setTreatments([...treatments, cleanedForm]);
      resetForm();
    }
  };

  const resetForm = () => {
    const now = getNow();
    setForm({
      date: now.date,
      time: now.time,
      bpSystolic: "",
      bpDiastolic: "",
      temp: "",
      pulse: "",
      spo2: "",
      weight: "",
      height: admissionData?.height || "",
      rr: "",
      bsl: "",
      diagnosis: admissionData?.diagnosis || "",
      complaint: "",
      remarks: "",
      details: [{ type: "", name: "", dose: "", instructions: "", allergy: "No" }],
    });
  };

  const handleEditTreatment = (index) => {
    setForm(treatments[index]);
    setEditIndex(index);
  };

  const handleCancelEdit = () => {
    resetForm();
    setEditIndex(null);
    setEditHistoricId(null);
  };

  const handleRemoveTreatment = (index) => {
    const updated = [...treatments];
    updated.splice(index, 1);
    setTreatments(updated);
  };

  const handleEditHistoric = (t) => {
    // Parse datetime
    const dateTimeStr = t.vitalDateTime || "";
    let d = "";
    let tm = "";
    if (dateTimeStr.includes(" ")) [d, tm] = dateTimeStr.split(" ");
    else if (dateTimeStr.includes("T")) [d, tm] = dateTimeStr.split("T");
    else d = dateTimeStr;

    // Parse details string
    const parts = (t.details || "").split(" | ");
    let diagnosis = "";
    let complaint = "";
    let remarks = "";
    let prescriptionStr = "";

    parts.forEach(p => {
      if (p.startsWith("Diagnosis: ")) diagnosis = p.replace("Diagnosis: ", "");
      else if (p.startsWith("Complaint: ")) complaint = p.replace("Complaint: ", "");
      else if (p.startsWith("Remarks: ")) remarks = p.replace("Remarks: ", "");
      else if (p.startsWith("Prescription: ")) prescriptionStr = p.replace("Prescription: ", "");
    });

    // Parse meds
    const meds = [];
    if (prescriptionStr) {
      const medParts = prescriptionStr.split("; ");
      medParts.forEach(m => {
        let type = "";
        let name = m;
        let dose = "";
        let instructions = "";

        if (m.includes(": ")) {
          const [first, ...rest] = m.split(": ");
          type = first;
          name = rest.join(": ");
        }

        if (name.includes(" - ")) {
          const lastDash = name.lastIndexOf(" - ");
          instructions = name.substring(lastDash + 3);
          name = name.substring(0, lastDash);
        }

        if (name.trim().endsWith(")")) {
          const firstParen = name.lastIndexOf(" (");
          if (firstParen > -1) {
            dose = name.substring(firstParen + 2, name.length - 1);
            name = name.substring(0, firstParen);
          }
        }
        meds.push({ type, name, dose, instructions, allergy: "No" });
      });
    }
    if (meds.length === 0) meds.push({ type: "", name: "", dose: "", instructions: "", allergy: "No" });

    setForm({
      date: d,
      time: tm ? tm.slice(0, 5) : "",
      bpSystolic: t.bpSystolic || "",
      bpDiastolic: t.bpDiastolic || "",
      temp: t.temperature || "",
      pulse: t.pulse || "",
      spo2: t.spo2 || "",
      weight: t.weight || "",
      rr: t.option1 || "", // Assuming option1 is RR
      bsl: t.option2 || "", // Assuming option2 is BSL
      diagnosis,
      complaint,
      remarks, // or try t.notes? t.notes is saved but details string usually has the display data
      details: meds
    });
    setEditHistoricId(t.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteHistoric = (id) => {
    setDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;

    // Optimistic Update Helper could be added here preferably, but we stick to logic
    const token = getAuthToken();
    if (!token || token === 'EXPIRED') return;

    try {
      const apiChar = getApiChara();
      const response = await fetch(`${apiChar}/ipd/vitals/${deleteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success("Record deleted successfully");
        if (admissionData) fetchHistoricTreatments(admissionData.ipdAdmissionId);
      } else {
        toast.error("Failed to delete record");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error deleting record");
    } finally {
      setShowDeleteConfirm(false);
      setDeleteId(null);
    }
  };

  const handleSubmitAll = async () => {
    // Check if there are ANY new items to save (treatments or prescribed medicines)
    if (treatments.length === 0 && prescribedMedicines.length === 0) {
      if (historicTreatments.length > 0) {
        toast.info("No new treatments or prescriptions to save.");
        navigate("/dashboard/ipd");
        return;
      } else {
        setValidationErrors(["Please add at least one treatment or prescription before submitting."]);
        setShowValidationModal(true);
        return;
      }
    }

    const token = getAuthToken();
    const apiChar = getApiChara();
    if (!token || token === "EXPIRED") {
      toast.error("Session expired, please login again");
      return;
    }

    setIsLoading(true);
    try {
      // 1. Save Vitals/Doctor Visit Records (treatments array)
      for (const t of treatments) {
        // Format medicine details for the vitals 'details' string
        const medDetails = t.details
          .filter(d => d.name)
          .map(d => `${d.type ? d.type + ": " : ""}${d.name}${d.dose ? ` (${d.dose})` : ""}${d.instructions ? ` - ${d.instructions}` : ""}`)
          .join("; ");

        const combinedDetails = [
          t.diagnosis ? `Diagnosis: ${t.diagnosis}` : "",
          t.complaint ? `Complaint: ${t.complaint}` : "",
          t.remarks ? `Remarks: ${t.remarks}` : "",
          medDetails ? `Prescription: ${medDetails}` : ""
        ].filter(Boolean).join(" | ");

        const vitalDateTime = `${t.date} ${t.time}:00`;

        const vitalsPayload = {
          id: 0,
          ipdAdmissionId: admissionData,
          vitalDateTime: vitalDateTime,
          details: combinedDetails || "N/A",
          height: String(t.height || admissionData.height || ""),
          weight: String(t.weight || admissionData.weight || ""),
          bpSystolic: Number(t.bpSystolic) || 0,
          bpDiastolic: Number(t.bpDiastolic) || 0,
          pulse: Number(t.pulse) || 0,
          temperature: Number(t.temp) || 0,
          spo2: Number(t.spo2) || 0,
          notes: t.remarks || t.complaint || "",
          option1: t.rr || "",
          option2: t.bsl || "",
          option3: ""
        };

        const vitalsResponse = await fetch(`${apiChar}/ipd/vitals`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(vitalsPayload),
        });

        if (!vitalsResponse.ok) {
          throw new Error("Failed to save doctor visit record");
        }

        // 1.1 Save each medicine as an individual prescription record
        for (const med of t.details) {
          if (!med.name.trim()) continue;

          const prescriptionPayload = {
            id: 0,
            patientId: admissionData.patientId,
            doctorId: admissionData.doctorId,
            ipdAdmissionId: admissionData,
            dateTime: vitalDateTime, // Use same time as the visit
            medicineName: med.name,
            breakFastDose: "",
            lunchDose: "",
            dinnerDose: "",
            notes: t.remarks || "",
            instruction: med.instructions || "",
            type: med.type || "",
            days: 0,
            dosage: med.dose || "",
            diagnosis: t.diagnosis || "",
            preNumber: 0,
            savePrescription: isSavePrescription
          };

          const prescriptionRes = await fetch(`${apiChar}/ipd/prescriptions`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(prescriptionPayload),
          });

          if (!prescriptionRes.ok) {
            console.warn("Failed to save individual prescription item for", med.name);
          }
        }
      }

      // 2. Save Detailed Prescriptions from Modal (if any exist independently)
      if (prescribedMedicines.length > 0) {
        const nowStr = new Date().toISOString().replace("T", " ").substring(0, 19);
        for (const med of prescribedMedicines) {
          const pPayload = {
            id: 0,
            patientId: admissionData.patientId,
            doctorId: admissionData.doctorId,
            ipdAdmissionId: admissionData,
            dateTime: nowStr,
            medicineName: med.medicineName,
            breakFastDose: med.breakFastDose || "",
            lunchDose: med.lunchDose || "",
            dinnerDose: med.dinnerDose || "",
            notes: "",
            instruction: med.instruction || med.instructions || "",
            type: med.type || "",
            days: Number(med.days) || 0,
            dosage: med.dosage || "",
            diagnosis: form.diagnosis || "",
            preNumber: 0,
            savePrescription: true
          };

          const modalRes = await fetch(`${apiChar}/ipd/prescriptions`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(pPayload),
          });

          if (!modalRes.ok) throw new Error("Failed to save modal prescription item");
        }
      }

      toast.success("All treatment and prescription records saved successfully!");
      setTreatments([]);
      setPrescribedMedicines([]);
      navigate("/dashboard/ipd");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Error submitting data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDischargeSubmit = async (formData) => {
    setIsLoading(true);
    try {
      const apiChar = getApiChara();
      const id = admissionData.ipdAdmissionId;
      const apiUrl = `${apiChar}/ipd/admissions/${id}/discharge`;
      const token = getAuthToken();

      const formatDateTime = (d, t) => {
        if (!d || !t) return null;
        return `${d} ${t}:00`;
      };

      // Construct the comprehensive discharge payload
      const payload = {
        ...admissionData,
        status: "DISCHARGED",
        dischargeDateTime: formatDateTime(formData.dod, formData.dodTime),
        weight: String(formData.weight || admissionData.weight || ""),
        diagnosis: formData.diagnosis || admissionData.diagnosis || "",
        notes: formData.summaryNotes || admissionData.notes || "",
        nextFollowupVisitToDr: formData.nextAppointment || admissionData.nextFollowupVisitToDr || "",
        nextAppointment: formData.nextAppointment || admissionData.nextAppointment || "",
        option2: formData.condition || admissionData.option2 || "",
        option3: formData.dischargeType || admissionData.option3 || "",
        option4: formData.paymentClearance || admissionData.option4 || "",
        option5: formData.medicoLegal || admissionData.option5 || "",
        drWrittenDischargeSummery: formData.drWrittenDischargeSummery || "",
        pho: formData.pho || admissionData.pho || "",
        co: formData.co || admissionData.co || "",
        kco: formData.kco || admissionData.kco || "",
        cf: formData.cf || admissionData.cf || ""
      };

      const response = await fetch(apiUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || "Failed to discharge patient");
      }

      toast.success("Patient discharged successfully!");
      setShowDischargeModal(false);
      navigate("/dashboard/ipd");
    } catch (err) {
      console.error(err);
      toast.error("Error discharging patient: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportPrescription = () => {
    if (prescribedMedicines.length === 0) {
      toast.warning("No medicines to export.");
      return;
    }
    exportIPDPrescription(mappedPatient, prescribedMedicines, hospitalData);
  };

  if (isLoading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" /> <p>Loading Patient Details...</p>
      </div>
    );
  }

  if (!admissionData) {
    return (
      <div className="text-center mt-5">
        <h4>No Admission Record Found</h4>
        <button className="btn btn-primary" onClick={() => navigate("/dashboard/ipd")}>Go Back</button>
      </div>
    );
  }

  // Helper for UI data mapping
  const mappedPatient = (() => {
    const calculateAge = (dob) => {
      if (!dob) return "-";
      const birthDate = new Date(dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    };

    // Fix Split logic for date and time
    const dateTimeStr = admissionData.admissionDateTime || "";
    let adDate = "-";
    let adTime = "-";

    if (dateTimeStr.includes("T")) {
      [adDate, adTime] = dateTimeStr.split("T");
    } else if (dateTimeStr.includes(" ")) {
      [adDate, adTime] = dateTimeStr.split(" ");
    } else {
      adDate = dateTimeStr;
    }

    if (adTime && adTime.length > 5) adTime = adTime.slice(0, 5);

    return {
      patientId: admissionData.patientId?.patientId,
      name: `${admissionData.patientId?.firstName} ${admissionData.patientId?.lastName}`,
      gender: admissionData.patientId?.gender,
      dob: admissionData.patientId?.dob || "-",
      age: calculateAge(admissionData.patientId?.dob) || admissionData.patientId?.age || "-",
      phone: admissionData.patientId?.mobile,
      address: admissionData.patientId?.address || "-",
      bloodGroup: admissionData.bloodGroup || "-",
      admissionDate: adDate,
      admissionTime: adTime,
      ipdAdmissionId: admissionData.ipdAdmissionId || "-",
      floor: admissionData.wardBed?.floor,
      wardType: `${admissionData.wardBed?.wardName} - ${admissionData.wardBed?.bedNumber}`,
      oldPatient: admissionData.patientId?.registrationNo ? "Yes" : "No",
      doctor: `${admissionData.doctorId?.firstName} ${admissionData.doctorId?.lastName}`,
      admissionReason: admissionData.admissionReason || "-",
      vitals: {
        height: admissionData.height || "-",
        weight: admissionData.weight || "-",
        bp: admissionData.bp || "-",
        temp: admissionData.temperature || "-",
        spo2: admissionData.spo2 || "-",
        pulse: admissionData.pulse || "-"
      },
      guardian: {
        name: admissionData.guardianName || "-",
        contact: admissionData.guardianContactNumber || "-",
        relation: admissionData.guardianRelation || "-"
      },
      symptoms: {
        type: "-",
        title: admissionData.symptomsTitle || "-",
        diagnosis: admissionData.diagnosis || "-",
        desc: admissionData.symptomsDesc || "-"
      },
      diagnosis: admissionData.diagnosis || "-",
      insurance: {
        tpaId: admissionData.tpaId || "-",
        tpaValidity: admissionData.tpaValidity || "-",
        insuranceCardNo: admissionData.insuranceCardNo || "-"
      },
      identities: {
        aadhar: admissionData.adharCardNo || admissionData.patientId?.adharCardNo || "-",
        pan: admissionData.panCardNo || admissionData.patientId?.panCardNo || "-"
      },
      bank: {
        account: admissionData.bankAccount || "-"
      },
      payment: {
        advance: admissionData.option2 || "0",
        type: admissionData.option3 || "-"
      },
      notes: admissionData.notes || "-",
      pho: admissionData.pho || "",
      co: admissionData.co || "",
      kco: admissionData.kco || "",
      cf: admissionData.cf || "",
      status: admissionData.status || "-",
      dischargeDateTime: admissionData.dischargeDateTime,
      dischargeType: admissionData.option3,
      conditionAtDischarge: admissionData.option2,
      paymentClearance: admissionData.option4,
      drWrittenDischargeSummery: admissionData.drWrittenDischargeSummery,
      nextAppointment: admissionData.nextAppointment || admissionData.next_appointment || ""
    };
  })();

  return (
    <div className={classes.container}>
      {/* Header */}
      <div className={classes.header}>
        <div className="d-flex align-items-center gap-3">
          {/* <button
            className="btn-secondary-premium p-0 d-flex align-items-center justify-content-center rounded-circle"
            style={{ width: '40px', height: '40px', minWidth: '40px' }}
            onClick={() => navigate("/dashboard/ipd")}
            title={t("Back to IPD List")}
          >
            <FaArrowLeft />
          </button> */}
          <div className={classes.patientInfo}>
            <h3>{t("Patient Name")} - {mappedPatient.name}</h3>
            <div className="d-flex gap-3 text-muted" style={{ fontSize: "0.9rem" }}>
              <span>{t("Mobile")}: {mappedPatient.phone}</span>
              <span>{t("IPD ID")}: <strong>{mappedPatient.ipdAdmissionId}</strong></span>
            </div>
          </div>
        </div>
        <div className={classes.actions}>
          <button
            className="btn-premium"
            onClick={() => setShowModal(true)}
          >
            <FaInfoCircle className="me-2" /> {t("Admission Details")}
          </button>
          <button
            className="btn-danger-premium"
            onClick={() => setShowDischargeModal(true)}
            disabled={admissionData?.status !== 'ACTIVE'}
            style={{ opacity: admissionData?.status !== 'ACTIVE' ? 0.6 : 1, cursor: admissionData?.status !== 'ACTIVE' ? 'not-allowed' : 'pointer' }}
          >
            <FaSignOutAlt className="me-2" /> {t("Discharge")}
          </button>
          <button
            className="btn-premium d-flex align-items-center gap-2"
            onClick={() => setShowPrescriptionModal(true)}
            disabled
          >
            <FaFilePrescription className="me-2" /> {t("Prescription")}
            {prescribedMedicines.length > 0 && (
              <span className="badge rounded-pill bg-success" style={{ fontSize: "0.7rem" }}>
                {prescribedMedicines.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Treatment Entry */}
      {admissionData.status === "ACTIVE" ? (
        <div className={classes.section}>
          <h3>{editIndex !== null ? "Edit Treatment Entry" : "Doctor Visit Entry"}</h3>

          <div className={classes.vitalsRow}>
            <input type="date" name="date" value={form.date} onChange={handleChange} className={classes.input} />
            <input type="time" name="time" value={form.time} onChange={handleChange} className={classes.input} />
            <input type="text" name="weight" placeholder={t("Weight (kg)")} value={form.weight} onChange={handleChange} className={classes.input} />
          </div>

          <div className={classes.vitalsRow}>
            <input type="number" name="bpSystolic" placeholder={t("BPS (Systolic)")} value={form.bpSystolic} onChange={handleChange} className={classes.input} title={t("Systolic BP")} />
            <input type="number" name="bpDiastolic" placeholder={t("BPD (Diastolic)")} value={form.bpDiastolic} onChange={handleChange} className={classes.input} title={t("Diastolic BP")} />
            <input type="text" name="temp" placeholder={t("Temp (°F)")} value={form.temp} onChange={handleChange} className={classes.input} />
            <input type="text" name="pulse" placeholder={t("Pulse (bpm)")} value={form.pulse} onChange={handleChange} className={classes.input} />
            <input type="text" name="spo2" placeholder={t("SPO2 (%)")} value={form.spo2} onChange={handleChange} className={classes.input} />
          </div>


          <div className={classes.diagnosisRow}>
            <div className={classes.inputWrapper}>
              <input
                type="text"
                name="diagnosis"
                placeholder={t("Enter Diagnosis")}
                value={form.diagnosis}
                onChange={handleDiagnosisChange}
                className={classes.input}
                list="ipd-diagnosis-list-entry"
                style={{ width: "100%" }}
              />
              <datalist id="ipd-diagnosis-list-entry">
                {diagnosisList.map((d, idx) => (
                  <option key={idx} value={d} />
                ))}
              </datalist>
            </div>
            <div className={classes.inputWrapper}>
              <input
                type="text"
                name="complaint"
                placeholder={t("Patient Complaint")}
                value={form.complaint}
                onChange={handleChange}
                className={classes.input}
                style={{ width: "100%" }}
              />
            </div>
            <div className={classes.inputWrapper}>
              <textarea
                name="remarks"
                placeholder={t("Doctor's Remarks")}
                value={form.remarks}
                onChange={handleChange}
                className={classes.input}
                rows={1}
                style={{ width: "100%", resize: 'none', paddingTop: '8px', height: 'auto', minHeight: '40px' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            {form.details.map((detail, index) => (
              <div key={index} className={classes.medicineRow}>
                <select name="type" value={detail.type} onChange={(e) => handleDetailChange(index, e)} className={classes.input}>
                  <option value="">{t("Select")}</option>
                  <option value="TAB">TAB</option>
                  <option value="CAP">CAP</option>
                  <option value="SYR">SYR</option>
                  <option value="INJ">INJ</option>
                  <option value="DROP">DROP</option>
                  <option value="EDRP">EDRP</option>
                  <option value="LIQ">LIQ</option>
                  <option value="OINT">OINT</option>
                  <option value="GEL">GEL</option>
                  <option value="SPRAY">SPRAY</option>
                  <option value="LOT">LOT</option>
                  <option value="POWDER">POWDER</option>
                </select>
                <input
                  type="text"
                  name="name"
                  placeholder={t("Search Medicine...")}
                  value={detail.name}
                  onChange={(e) => handleDetailChange(index, e)}
                  className={classes.input}
                  list="ipd-history-meds-list"
                />
                <datalist id="ipd-history-meds-list">
                  {searchResults.map((m, idx) => (
                    <option key={idx} value={m.name} />
                  ))}
                </datalist>
                <input type="text" name="dose" placeholder={t("Dose")} value={detail.dose} onChange={(e) => handleDetailChange(index, e)} className={classes.input} />
                <input type="text" name="instructions" placeholder={t("Instructions")} value={detail.instructions} onChange={(e) => handleDetailChange(index, e)} className={classes.input} />
                {/* <select name="allergy" value={detail.allergy} onChange={(e) => handleDetailChange(index, e)} className={classes.input}>
                <option value="">Allergy?</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select> */}
                {index === form.details.length - 1 ? (
                  <button type="button" className={classes.addMedicineBtn} onClick={handleAddDetailRow}>
                    <FaPlusCircle />
                  </button>
                ) : (
                  <button type="button" className={classes.addMedicineBtn} style={{ backgroundColor: '#e53e3e' }} onClick={() => handleRemoveDetailRow(index)}>
                    <FaMinusCircle />
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className={classes.formRow}>
            {(editIndex !== null || editHistoricId !== null) && (
              <button onClick={handleCancelEdit} className="btn-secondary-premium me-2">
                <FaTimes className="me-2" /> {t("Cancel")}
              </button>
            )}
            <button className="btn-premium d-flex align-items-center" onClick={handleAddTreatment} disabled={isLoading}>
              {isLoading ? <Spinner animation="border" size="sm" className="me-2" /> : <FaPlusCircle className="me-2" />}
              {editHistoricId !== null ? t("Update Visit Record") : editIndex !== null ? t("Update Entry") : t("Add Record")}
            </button>
          </div>
        </div>
      ) : (
        <div className={classes.section + " bg-light border-0 text-center py-4"}>
          <div className="d-inline-flex align-items-center justify-content-center p-3 rounded-circle bg-warning bg-opacity-10 text-warning mb-3">
            <FaSignOutAlt size={24} />
          </div>
          <h5 className="text-secondary fw-bold mb-1">{t("Patient is")} {t(admissionData.status)}</h5>
          <p className="text-muted small mb-0">{t("New treatment entries are disabled for this patient.")}</p>
        </div>
      )}


      {/* Prescription section removed from here and moved to Modal */}



      {/* Treatment History Table */}
      <div className={classes.section}>
        <h3>{t("Treatment History")}</h3>
        {(historicTreatments.length > 0 || treatments.length > 0) ? (
          <div className="table-responsive">
            <table className={classes.table}>
              <thead>
                <tr>
                  <th style={{ width: '120px' }}>{t("Date & Time")}</th>
                  <th>{t("Vitals")}</th>
                  <th>{t("Treatment Details")}</th>
                  <th>{t("Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {/* Render Historic Treatments First */}
                {historicTreatments.map((treatment, i) => {
                  const dateTimeStr = treatment.vitalDateTime || "";
                  let date = "-";
                  let time = "-";
                  if (dateTimeStr.includes(" ")) {
                    [date, time] = dateTimeStr.split(" ");
                  } else if (dateTimeStr.includes("T")) {
                    [date, time] = dateTimeStr.split("T");
                  } else {
                    date = dateTimeStr;
                  }
                  return (
                    <tr key={`historic-${i}`} style={{ backgroundColor: "#f8f9fa" }}>
                      <td>
                        <div style={{ whiteSpace: 'nowrap' }}>
                          <b>{date}</b> {time ? time.slice(0, 5) : "-"}
                        </div>
                      </td>
                      <td>
                        <div className={classes.vitalsDisplay}>
                          <span><b>BPS:</b> {treatment.bpSystolic || "-"}</span>
                          <span><b>BPD:</b> {treatment.bpDiastolic || "-"}</span>
                          <span><b>Temp:</b> {treatment.temperature}°F</span>
                          <span><b>Pulse:</b> {treatment.pulse}</span>
                          <span><b>SPO2:</b> {treatment.spo2}%</span>
                          <span><b>Wt:</b> {treatment.weight} kg</span>
                          {treatment.rr && <span><b>RR:</b> {treatment.rr}</span>}
                          {treatment.bsl && <span><b>BSL:</b> {treatment.bsl}</span>}
                        </div>
                      </td>
                      <td>
                        <span className={classes.savedBadge}>{treatment.details || "-"}</span>
                      </td>
                      <td>
                        {userRole === 'ROLE_DOCTOR' && admissionData.status === 'ACTIVE' ? (
                          <div className="d-flex gap-1 justify-content-center">
                            <button className="btn-premium btn-sm" onClick={() => handleEditHistoric(treatment)} title={t("Edit")}>
                              <FaEdit />
                            </button>
                            <button className="btn-danger-premium btn-sm" onClick={() => handleDeleteHistoric(treatment.id)} title={t("Delete")}>
                              <FaTrash />
                            </button>
                          </div>
                        ) : (
                          <span>-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {/* Render New (Unsaved) Treatments */}
                {treatments.map((treatment, i) => (
                  <tr key={`new-${i}`}>
                    <td>
                      <div style={{ whiteSpace: 'nowrap' }}>
                        <b>{treatment.date}</b> {treatment.time}
                      </div>
                    </td>
                    <td>
                      <div className={classes.vitalsDisplay}>
                        <span><b>BP:</b> {treatment.bpSystolic || "-"}/{treatment.bpDiastolic || "-"}</span>
                        <span><b>Temp:</b> {treatment.temp}°F</span>
                        <span><b>Pulse:</b> {treatment.pulse}</span>
                        <span><b>SPO2:</b> {treatment.spo2}%</span>
                        <span><b>Wt:</b> {treatment.weight}kg</span>
                      </div>
                    </td>
                    <td>
                      <div className={classes.medList}>
                        {(treatment.diagnosis || treatment.complaint || treatment.remarks) && (
                          <div style={{ marginBottom: '8px', padding: '5px', backgroundColor: '#f0f9f9', borderRadius: '4px', fontSize: '12px' }}>
                            {treatment.diagnosis && <div><b>{t("Diagnosis")}:</b> {treatment.diagnosis}</div>}
                            {treatment.complaint && <div><b>{t("Patient Complaint")}:</b> {treatment.complaint}</div>}
                            {treatment.remarks && <div><b>{t("Doctor's Remarks")}:</b> {treatment.remarks}</div>}
                          </div>
                        )}
                        {treatment.details.map((d, j) => (
                          <div key={j} className={classes.medItem}>
                            <div style={{ fontWeight: '600', fontSize: '12px', lineHeight: '1.2' }}>
                              {d.type && <span style={{ color: '#006d77' }}>{d.type}: </span>}
                              {d.name} {d.dose && <small>({d.dose})</small>}
                            </div>
                            {d.instructions && (
                              <div className="text-muted" style={{ fontSize: '11px', marginTop: '1px' }}>
                                Ins:{d.instructions}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </td>
                    <td>
                      {editIndex === i ? (
                        <button className={classes.iconBtn} onClick={handleCancelEdit}>
                          <FaTimes title={t("Cancel Edit")} color="#e53e3e" />
                        </button>
                      ) : (
                        <div className="d-flex gap-1 justify-content-center">
                          <button
                            className="btn-premium btn-sm"
                            onClick={() => handleEditTreatment(i)}
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="btn-danger-premium btn-sm"
                            onClick={() => handleRemoveTreatment(i)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No treatments recorded yet.</p>
        )}
      </div>

      {/* 🔹 Action Footer */}
      {(historicTreatments.length > 0 || treatments.length > 0 || prescribedMedicines.length > 0) && (
        <div className={classes.section + " bg-light p-3 rounded d-flex justify-content-end"}>
          <div className={classes.footerActions}>
            <button className="btn-secondary-premium px-4 py-2 d-flex align-items-center" onClick={() => navigate("/dashboard/ipd")}>
              <FaArrowLeft className="me-2" /> {t("Back")}
            </button>

            <IPDPatientDischargePrint
              patientData={mappedPatient}
              treatments={[...historicTreatments, ...treatments]}
              hospitalData={{ ...hospitalData, doctor: mappedPatient.doctor }}
            />

            <div className="d-flex align-items-center gap-2">
              <button
                className="btn-export-premium"
                onClick={() => {
                  const allTreatments = [...historicTreatments, ...treatments];
                  exportTreatmentPDF(mappedPatient, allTreatments, authCtx, "A4");
                }}
              >
                <FaFileExport className="me-2" /> {t("Export")}
              </button>
            </div>


            <button className="btn-premium px-4 py-2 d-flex align-items-center" onClick={handleSubmitAll} disabled={isLoading || admissionData.status !== 'ACTIVE'}>
              {isLoading ? <Spinner animation="border" size="sm" className="me-2" /> : <FaSave className="me-2" />}
              {isLoading ? t("Saving...") : t("Save All Records")}
            </button>
          </div>
        </div>
      )}

      {/* Vitals Trend Section */}
      <VitalsTrendChart
        treatments={treatments}
        historicTreatments={historicTreatments}
        patientData={mappedPatient}
        authCtx={authCtx}
      />

      {/* Modals */}
      <AdmissionDetailsModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          fetchAdmission();
        }}
        patient={mappedPatient}
        admissionData={admissionData}
        patientId={ipdPatientId}
      />
      <DischargeModal
        show={showDischargeModal}
        onClose={() => setShowDischargeModal(false)}
        patient={mappedPatient}
        hospitalData={hospitalData}
        onSubmit={handleDischargeSubmit}
      />
      <PrescriptionModal
        show={showPrescriptionModal}
        onClose={() => setShowPrescriptionModal(false)}
        prescribedMedicines={prescribedMedicines}
        setPrescribedMedicines={setPrescribedMedicines}
        patientData={mappedPatient}
        admissionData={admissionData}
        hospitalData={hospitalData}
        isSavePrescription={isSavePrescription}
        setIsSavePrescription={setIsSavePrescription}
        isPrintPrescription={isPrintPrescription}
        setIsPrintPrescription={setIsPrintPrescription}
      />
      <ValidationModal show={showValidationModal} errors={validationErrors} onClose={() => setShowValidationModal(false)} />
      {/* Confirmation Modal */}
      <ConfirmModal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title={t("Delete Record")}
        message={t("Are you sure you want to delete this treatment record?")}
        variant="danger"
      />
    </div>
  );
};

export default IPDPatientDischarge;
